import { FeatureItemValueTypeEnum } from '../modules/base/enums/feature-item-value-type.enum';
import { FeatureItemValueNormalizeError } from '../modules/base/errors/feature-item-value-normalize.error';

function isNumericString(value: string): boolean {
  if (value.trim() === '') {
    return false;
  }
  return Number.isFinite(Number(value));
}

function normalizeNumber(value: unknown): string {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      throw new FeatureItemValueNormalizeError(
        'feature item value must be a finite number',
      );
    }
    return String(value);
  }
  if (typeof value === 'string' && isNumericString(value)) {
    return String(Number(value));
  }
  throw new FeatureItemValueNormalizeError(
    'feature item value must be a number or numeric string',
  );
}

function normalizeBool(value: unknown): string {
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (value === 'true' || value === 'false') {
    return value;
  }
  throw new FeatureItemValueNormalizeError(
    "feature item value must be a boolean or 'true'/'false'",
  );
}

function normalizeString(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  throw new FeatureItemValueNormalizeError(
    'feature item value must be a string, number, or boolean',
  );
}

function normalizeJson(value: unknown): string {
  if (typeof value === 'string') {
    try {
      JSON.parse(value);
      return value;
    } catch {
      throw new FeatureItemValueNormalizeError(
        'feature item value must be valid JSON',
      );
    }
  }
  if (value !== null && typeof value === 'object') {
    return JSON.stringify(value);
  }
  throw new FeatureItemValueNormalizeError(
    'feature item value must be a JSON object/array or JSON string',
  );
}

function normalizeWithoutType(value: unknown): string {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value);
  }
  throw new FeatureItemValueNormalizeError(
    'feature item value must be a string, number, or boolean',
  );
}

/**
 * Normalize a feature item value for string storage according to its valueType.
 * Accepts native JS types from clients (e.g. number inputs) and returns the
 * string form stored in the DB. Passes through null, empty string, and undefined.
 */
export function normalizeFeatureItemValue(
  value: unknown,
  valueType?: FeatureItemValueTypeEnum | null,
): string | null | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  if (value === '') {
    return '';
  }

  switch (valueType) {
    case FeatureItemValueTypeEnum.NUMBER:
      return normalizeNumber(value);
    case FeatureItemValueTypeEnum.BOOL:
      return normalizeBool(value);
    case FeatureItemValueTypeEnum.JSON:
      return normalizeJson(value);
    case FeatureItemValueTypeEnum.STRING:
      return normalizeString(value);
    default:
      return normalizeWithoutType(value);
  }
}
